import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDataTable, SpecraOverlayDrawer } from '@mono/ui-components/captains';
import { SpecraButton, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraFormField, SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
import { cents } from './ratesView';
const T = Testids.pricingWorkspace;
function useComparisonSide(scope, scenario) {
    return useQuery({
        queryKey: [
            'pricing-compare',
            scope.principal,
            scope.projectId,
            scenario?.id,
            scope.period,
            'all-periods',
            scenario?.updatedAt
        ],
        enabled: !!scope.principal && !!scenario,
        retry: false,
        staleTime: 0,
        queryFn: async ({ signal })=>{
            const request = {
                projectId: scope.projectId,
                scenarioId: scenario?.id ?? ''
            };
            const [totals, first] = await Promise.all([
                projectDataClient.computePricingTotals(request, {
                    signal
                }),
                projectDataClient.getPricingRateBook({
                    ...request,
                    periodKey: 'base'
                }, {
                    signal
                })
            ]);
            const books = [
                first,
                ...await Promise.all(first.periods.filter((p)=>p.periodKey !== first.periodKey).map((p)=>projectDataClient.getPricingRateBook({
                        ...request,
                        periodKey: p.periodKey
                    }, {
                        signal
                    })))
            ];
            if (books.some((book)=>book.scenarioId !== request.scenarioId || book.scenarioUpdatedAt !== first.scenarioUpdatedAt)) {
                throw new Error('Pricing changed while comparing. Retry to load the latest values.');
            }
            return {
                total: cents(totals.totalCents),
                periods: [
                    ...new Set([
                        ...books.map((b)=>b.periodKey),
                        ...Object.keys(totals.byPeriod)
                    ])
                ].map((key)=>({
                        key,
                        label: first.periods.find((p)=>p.periodKey === key)?.label || key,
                        total: cents(totals.byPeriod[key])
                    })),
                rates: books.flatMap((book)=>{
                    const identity = (row)=>row.link?.lcatLogicalKey ? `logical:${row.link.lcatLogicalKey}` : `category:${row.categoryId}`;
                    return book.rows.map((row)=>{
                        const id = identity(row);
                        // Never pair ambiguous categories by position or title.
                        const unique = !!(row.link?.lcatLogicalKey || row.categoryId) && book.rows.filter((r)=>identity(r) === id).length === 1;
                        if (!unique) throw new Error('Pricing categories have ambiguous identifiers. Reload the scenarios before comparing.');
                        return {
                            key: JSON.stringify([
                                book.periodKey,
                                id
                            ]),
                            label: `${row.title} · ${first.periods.find((p)=>p.periodKey === book.periodKey)?.label || book.periodKey} · Using`,
                            amount: row.using?.amount?.trim() || undefined
                        };
                    });
                })
            };
        }
    });
}
function rate(amount) {
    return amount === undefined ? 'Not set' : Number(amount).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4
    });
}
export function CompareScenariosDrawer({ scope, scenarios, onClose }) {
    const sorted = [
        ...scenarios
    ].sort((a, b)=>(Date.parse(b.updatedAt) || 0) - (Date.parse(a.updatedAt) || 0) || a.id.localeCompare(b.id));
    const primary = scenarios.find((s)=>s.isPrimary) ?? sorted[0];
    const other = sorted.find((s)=>s.id !== primary?.id);
    const [selectedA, setA] = useState(primary?.id ?? '');
    const [selectedB, setB] = useState(other?.id ?? '');
    const a = scenarios.find((s)=>s.id === selectedA) ?? primary;
    const b = scenarios.find((s)=>s.id === selectedB) ?? other;
    const missing = !!selectedA && a?.id !== selectedA || !!selectedB && b?.id !== selectedB;
    const comparable = scenarios.length > 1 && a?.id !== b?.id;
    const left = useComparisonSide(scope, comparable ? a : undefined);
    const right = useComparisonSide(scope, comparable ? b : undefined);
    const failure = left.error || right.error;
    const loading = left.isFetching || right.isFetching || left.isPending || right.isPending;
    const rows = [];
    if (comparable && !failure && !loading && left.data && right.data) {
        rows.push({
            id: 'total',
            label: 'Total contract value',
            a: left.data.total,
            b: right.data.total
        });
        for (const key of new Set([
            ...left.data.periods,
            ...right.data.periods
        ].map((p)=>p.key))){
            const l = left.data.periods.find((p)=>p.key === key);
            const r = right.data.periods.find((p)=>p.key === key);
            rows.push({
                id: `period:${key}`,
                label: `Total · ${l?.label || r?.label}`,
                a: l?.total ?? '—',
                b: r?.total ?? '—'
            });
        }
        const l = new Map(left.data.rates.map((r)=>[
                r.key,
                r
            ]));
        const r = new Map(right.data.rates.map((r)=>[
                r.key,
                r
            ]));
        for (const key of new Set([
            ...l.keys(),
            ...r.keys()
        ])){
            const x = l.get(key);
            const y = r.get(key);
            if (x?.amount === y?.amount || x?.amount !== undefined && y?.amount !== undefined && Number(x.amount) === Number(y.amount)) continue;
            rows.push({
                id: `rate:${key}`,
                label: x?.label || y?.label || key,
                a: rate(x?.amount),
                b: rate(y?.amount)
            });
        }
    }
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: true,
        title: "Compare scenarios",
        width: "lg",
        testId: T.compareDrawer,
        description: "Current server totals and differing Using rates across all periods.",
        onOpenChange: (open)=>{
            if (!open) onClose();
        },
        children: scenarios.length < 2 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
            title: "Two scenarios are needed to compare"
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Scenario A",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        "aria-label": "Scenario A",
                        "data-testid": T.compareA,
                        value: a?.id ?? '',
                        onChange: (e)=>setA(e.target.value),
                        children: scenarios.map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s.id,
                                children: s.scenarioName
                            }, s.id))
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Scenario B",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        "aria-label": "Scenario B",
                        "data-testid": T.compareB,
                        value: b?.id ?? '',
                        onChange: (e)=>setB(e.target.value),
                        children: scenarios.map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s.id,
                                children: s.scenarioName
                            }, s.id))
                    })
                }),
                missing && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    children: "A selected scenario is no longer available. Default scenarios are selected."
                }),
                !comparable ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "info",
                    children: "Choose two different scenarios to compare."
                }) : !scope.principal ? /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Pricing access is unavailable."
                }) : failure ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    children: [
                        "Comparison unavailable: ",
                        failure.message,
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.compareRetry,
                            onClick: ()=>{
                                void left.refetch();
                                void right.refetch();
                            },
                            children: "Retry"
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                    testId: T.compareTable,
                    loading: loading,
                    loadingLabel: "Loading scenario comparison…",
                    rows: rows,
                    rowKey: (r)=>r.id,
                    getRowLabel: (r)=>r.label,
                    columns: [
                        {
                            id: 'metric',
                            header: 'Metric',
                            cell: (r)=>r.label
                        },
                        {
                            id: 'a',
                            header: `A · ${a?.scenarioName}`,
                            cell: (r)=>r.a
                        },
                        {
                            id: 'b',
                            header: `B · ${b?.scenarioName}`,
                            cell: (r)=>r.b
                        }
                    ]
                })
            ]
        })
    });
}
